import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  LibraryRepository,
  LibraryQueryParams,
  PaginatedLibraries
} from '@service/library/library-repository';
import { Library, LibraryMetrics } from '@model/library/library.model';
import { PolymorphicLibrary } from '@model/library/library-types.model';
import {
  LibrarySpecimen,
  LibrarySpecimenInput
} from '@model/library/base/library-specimen.model';
import {
  LibraryBackendMapper,
  LibraryBackendDto as LibraryBackendMapperDto,
  LibraryNGSBackendDto,
  LibraryMicroarrayBackendDto,
  LibraryMLPABackendDto,
  LibraryTripletBackendDto,
  LibrarySpecimenBackendDto as MapperSpecimenDto
} from '@model/library/mappers/library-backend-mapper';
import {
  LibraryForEdit,
  LibraryEditSpecimen
} from '@model/library/library-edit.model';
import {
  UpdateLibraryCommand,
  isUpdateLibraryNGSCommand,
  isUpdateLibraryMicroarrayCommand,
  isUpdateLibraryMLPACommand,
  isUpdateLibraryTripletExpansionCommand
} from '@model/library/commands/library-update-command';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import { environment } from '@env/environment';
import {
  LibraryBackendDto,
  LibraryBackendResponse,
  CreatedLibraryBackendDto,
  LibrarySpecimenBackendDto,
  LibraryEditBackendDto,
  LibraryEditSpecimenBackendDto
} from './library-backend.dto';
import { Technology, parseTechnology } from '@common/technology.enum';

/**
 * HTTP implementation of LibraryRepository
 * Connects to real backend API at http://localhost:8081/api/laboratorio/libreria/list
 *
 * This adapter follows the Dependency Inversion Principle:
 * - Domain layer defines the interface (LibraryRepository)
 * - Infrastructure layer provides the implementation (HttpLibraryRepository)
 */
@Injectable({
  providedIn: 'root'
})
export class HttpLibraryRepository extends LibraryRepository {
  private readonly httpClient = inject(HttpClient);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly baseUrl = `${environment.laboratory.baseUrl}${environment.laboratory.context}/library`;

  /**
   * Get paginated list of libraries from real backend
   * Endpoint: GET /api/laboratorio/library?page=0&size=10
   *
   * @param params Query parameters for pagination, search and filtering
   * @returns Observable of paginated libraries
   */
  getLibraries(params?: LibraryQueryParams): Observable<PaginatedLibraries> {
    const httpParams = this.buildHttpParams(params);

    return this.httpClient
      .get<LibraryBackendResponse>(this.baseUrl, {
        params: httpParams
      })
      .pipe(
        map((response) => this.mapToDomain(response)),
        catchError((error) => {
          return this.errorHandler.handleError('getLibraries', error);
        })
      );
  }

  /**
   * Get dashboard metrics for libraries
   * Endpoint: GET /api/laboratorio/libreria/metrics?date=2025-12-04
   *
   * @param date Optional date (defaults to today)
   * @returns Observable of library metrics
   */
  getMetrics(date?: Date | string): Observable<LibraryMetrics> {
    let httpParams = new HttpParams();

    if (date) {
      const dateStr =
        date instanceof Date ? date.toISOString().split('T')[0] : date;
      httpParams = httpParams.set('date', dateStr);
    }

    return this.httpClient
      .get<LibraryMetrics>(`${this.baseUrl}/metrics`, { params: httpParams })
      .pipe(
        catchError((error) => {
          return this.errorHandler.handleError('getMetrics', error);
        })
      );
  }

  /**
   * Get all libraries with CREATED status for a specific cell
   * Endpoint: GET /api/laboratorio/libraries/cell/{cellId}/created
   *
   * @param cellId The cell UUID to filter libraries by
   * @returns Observable of polymorphic libraries array
   */
  getLibrariesByCellIdCreated(
    cellId: string
  ): Observable<PolymorphicLibrary[]> {
    const url = `${this.baseUrl}/cell/${cellId}/created`;

    return this.httpClient.get<CreatedLibraryBackendDto[]>(url).pipe(
      map((response) =>
        this.mapCreatedLibrariesToPolymorphic(response, Technology.NGS)
      ),
      catchError((error) => {
        return this.errorHandler.handleError(
          'getLibrariesByCellIdCreated',
          error
        );
      })
    );
  }

  /**
   * Get all libraries with CREATED status for a specific technology
   * Used for Microarreglos runs where libraries are loaded by technology instead of cell.
   *
   * Endpoint: GET /api/laboratorio/libraries/technology/{technology}/created
   *
   * @param technology The technology to filter libraries by (e.g., 'MICROARREGLOS')
   * @returns Observable of polymorphic libraries array
   */
  getLibrariesByTechnologyCreated(
    technology: string
  ): Observable<PolymorphicLibrary[]> {
    const url = `${this.baseUrl}/technology/${technology}/created`;
    const tech = parseTechnology(technology) ?? Technology.NGS;

    return this.httpClient.get<CreatedLibraryBackendDto[]>(url).pipe(
      map((response) => this.mapCreatedLibrariesToPolymorphic(response, tech)),
      catchError((error) => {
        return this.errorHandler.handleError(
          'getLibrariesByTechnologyCreated',
          error
        );
      })
    );
  }

  /**
   * Build HTTP params from query parameters
   * Maps frontend params to backend API parameters
   */
  private buildHttpParams(params?: LibraryQueryParams): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    // Map of param keys to their backend names and values
    const paramMappings: [string, string | number | undefined][] = [
      ['page', params.page?.toString()],
      ['size', params.size?.toString()],
      ['sortBy', params.sortBy],
      ['sortDirection', params.sortDirection],
      ['searchField', params.searchField],
      ['search', params.searchValue], // Backend uses 'search' parameter
      ['status', params.status],
      ['technology', params.technology]
    ];

    // Apply all defined params
    paramMappings.forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  /**
   * Map backend response to domain model
   * Transforms DTOs to domain entities with proper types
   */
  private mapToDomain(response: LibraryBackendResponse): PaginatedLibraries {
    return {
      libraries: response.content.map((dto) => this.mapLibraryDto(dto)),
      pagination: {
        page: response.pageable.page,
        size: response.pageable.size,
        total: response.pageable.totalElements,
        totalPages: response.pageable.totalPages
      }
    };
  }

  /**
   * Map individual library DTO to domain model
   * Converts backend English field names to domain model
   */
  private mapLibraryDto(dto: LibraryBackendDto): Library {
    return {
      id: dto.id,
      code: dto.code,
      createdAt: new Date(dto.createdAt),
      equipment: dto.equipment || '',
      cell: dto.cell || '',
      samples: dto.samples || '',
      status: dto.status,
      statusDisplay: dto.statusDisplay || dto.status || '',
      createdBy: dto.createdBy || '',
      testCode: dto.testCode ?? undefined,
      permissions: dto.permissions
    };
  }

  /**
   * Map created libraries backend response to polymorphic library array
   * Converts infrastructure DTOs to domain DTOs and uses LibraryBackendMapper
   */
  private mapCreatedLibrariesToPolymorphic(
    dtos: CreatedLibraryBackendDto[],
    technology: Technology
  ): PolymorphicLibrary[] {
    return dtos.map((dto) => {
      // Convert infrastructure DTO to domain mapper DTO format
      const mapperDto = this.toMapperDto(dto, technology);
      return LibraryBackendMapper.fromBackendDto(mapperDto);
    });
  }

  /**
   * Convert infrastructure DTO to domain mapper DTO format
   * Returns the correctly typed DTO based on technology
   */
  private toMapperDto(
    dto: CreatedLibraryBackendDto,
    technology: Technology
  ): LibraryBackendMapperDto {
    const baseDto = {
      libraryId: dto.libraryId,
      libraryCode: dto.libraryCode,
      status: dto.status,
      sampleCount: dto.sampleCount,
      specimens: this.mapSpecimensToMapperFormat(dto.specimens),
      createdAt: dto.createdAt,
      updatedAt: dto.createdAt,
      createdByUserId: dto.createdByUserId,
      updatedByUserId: null
    };

    switch (technology) {
      case Technology.NGS:
        return {
          ...baseDto,
          technology: 'NGS',
          cellId: undefined,
          plateId: null,
          indexId: null
        } as LibraryNGSBackendDto;

      case Technology.MICROARREGLOS:
        return {
          ...baseDto,
          technology: 'MICROARREGLOS',
          chipType: dto.chipType ?? undefined,
          arrangementType: dto.arrangementType ?? undefined,
          plateName: dto.plateName,
          chipLot: dto.chipLot,
          chipReference: dto.chipReference
        } as LibraryMicroarrayBackendDto;

      case Technology.MLPA:
        return {
          ...baseDto,
          technology: 'MLPA',
          equipment: dto.equipment,
          creationDate: dto.creationDate,
          plateName: dto.plateName,
          batchInfo: dto.batchInfo ? { ...dto.batchInfo, position: null } : null
        } as LibraryMLPABackendDto;

      case Technology.EXPANSION_TRIPLETAS:
        return {
          ...baseDto,
          technology: 'EXPANSION_TRIPLETAS',
          equipment: dto.equipment,
          creationDate: dto.creationDate,
          plateName: dto.plateName,
          batchInfo: dto.batchInfo ? { ...dto.batchInfo, position: null } : null
        } as LibraryTripletBackendDto;

      default:
        throw new Error(`Unknown technology: ${technology}`);
    }
  }

  /**
   * Map specimens to mapper format
   */
  private mapSpecimensToMapperFormat(
    specimens: CreatedLibraryBackendDto['specimens']
  ): MapperSpecimenDto[] {
    return specimens.map((s) => ({
      libSpecimenId: s.libSpecimenId,
      specimenId: s.specimenId,
      indexAdapterId: s.indexId,
      indexValue: s.indexValue,
      lgCode: s.lgCode,
      position: null,
      assignmentDate: s.assignmentDate
    }));
  }

  /**
   * Map specimen DTO to new polymorphic specimen model
   */
  private mapSpecimenDto(dto: LibrarySpecimenBackendDto): LibrarySpecimen {
    return {
      libSpecimenId: dto.libSpecimenId,
      specimenId: dto.specimenId,
      indexAdapterId: dto.indexId,
      indexValue: dto.indexValue,
      lgCode: dto.lgCode,
      position: null,
      assignmentDate: dto.assignmentDate ? new Date(dto.assignmentDate) : null
    };
  }

  /**
   * Get library details for edit screen
   * Endpoint: GET /api/laboratorio/library/{libraryId}/edit
   *
   * @param libraryId The library UUID
   * @returns Observable of library with specimens for editing
   */
  getLibraryForEdit(libraryId: string): Observable<LibraryForEdit> {
    const url = `${this.baseUrl}/${libraryId}/edit`;

    return this.httpClient.get<LibraryEditBackendDto>(url).pipe(
      map((response) => this.mapLibraryEditToModel(response)),
      catchError((error) => {
        return this.errorHandler.handleError('getLibraryForEdit', error);
      })
    );
  }

  /**
   * Partially update library specimens (PATCH semantics)
   * Endpoint: PATCH /api/laboratorio/library/{libraryId}/specimens
   *
   * Only fields present in the command are sent to backend.
   * Backend preserves existing values for omitted fields (COALESCE pattern).
   *
   * @param command Update command (typed by technology)
   * @returns Observable of updated library
   */
  updateLibrarySpecimens(
    command: UpdateLibraryCommand
  ): Observable<LibraryForEdit> {
    const url = `${this.baseUrl}/${command.libraryId}/specimens`;

    // Transform command to backend format with only dirty fields
    const backendRequest = this.commandToBackendRequest(command);

    return this.httpClient
      .patch<LibraryEditBackendDto>(url, backendRequest)
      .pipe(
        map((response) => this.mapLibraryEditToModel(response)),
        catchError((error) => {
          return this.errorHandler.handleError('updateLibrarySpecimens', error);
        })
      );
  }

  /**
   * Transform update command to backend request format.
   *
   * PATCH SEMANTICS: Only includes fields that have values (not undefined).
   * Fields with undefined values are omitted from the request.
   *
   * MODOS DE ACTUALIZACIÓN:
   * - FULL_REPLACEMENT: specimens[] - Reemplaza todos
   * - INCREMENTAL: addedSpecimens[] + removedLgCodes[] - Solo agrega/elimina
   * - METADATA_ONLY: ninguno - Solo metadatos
   */
  private commandToBackendRequest(
    command: UpdateLibraryCommand
  ): Record<string, unknown> {
    const request = this.buildBaseRequest(command);
    this.addTechnologyFields(request, command);
    return request;
  }

  /**
   * Build base request with common fields (specimens, incremental updates).
   */
  private buildBaseRequest(
    command: UpdateLibraryCommand
  ): Record<string, unknown> {
    const request: Record<string, unknown> = {};

    if (command.specimens !== undefined) {
      request['specimens'] = command.specimens.map(
        this.mapSpecimenForBackend
      );
    }

    if (command.addedSpecimens?.length) {
      request['addedSpecimens'] = command.addedSpecimens.map(
        this.mapSpecimenForBackend
      );
    }

    if (command.removedLgCodes?.length) {
      request['removedLgCodes'] = command.removedLgCodes;
    }

    return request;
  }

  /**
   * Add technology-specific fields to the request.
   */
  private addTechnologyFields(
    request: Record<string, unknown>,
    command: UpdateLibraryCommand
  ): void {
    if (isUpdateLibraryNGSCommand(command)) {
      this.setIfDefined(request, 'cellId', command.cellId);
      this.setIfDefined(request, 'plateId', command.plateId);
      this.setIfDefined(request, 'indexId', command.indexId);
      return;
    }

    if (isUpdateLibraryMicroarrayCommand(command)) {
      this.setIfDefined(request, 'libraryCode', command.libraryCode);
      this.setIfDefined(request, 'chipType', command.chipType);
      this.setIfDefined(request, 'arrangementType', command.arrangementType);
      this.setIfDefined(request, 'plateName', command.plateName);
      this.addBatchInfoFields(request, command.batchInfo);
      return;
    }

    if (
      isUpdateLibraryMLPACommand(command) ||
      isUpdateLibraryTripletExpansionCommand(command)
    ) {
      this.setIfDefined(request, 'plateName', command.plateName);
      this.addBatchInfoFields(request, command.batchInfo);
    }
  }

  /**
   * Map a specimen input to the backend format.
   */
  private readonly mapSpecimenForBackend = (
    s: LibrarySpecimenInput
  ): Record<string, unknown> => ({
    lgCode: s.lgCode,
    indexAdapterId: s.indexAdapterId || null,
    position: s.position || null
  });

  /**
   * Set a key in the request only if the value is defined.
   */
  private setIfDefined(
    request: Record<string, unknown>,
    key: string,
    value: unknown
  ): void {
    if (value !== undefined) {
      request[key] = value;
    }
  }

  /**
   * Add batch info fields (chipLot, chipReference) to the request if present.
   */
  private addBatchInfoFields(
    request: Record<string, unknown>,
    batchInfo?: Record<string, unknown> | { lot?: unknown; reference?: unknown }
  ): void {
    if (!batchInfo) {
      return;
    }
    if ('lot' in batchInfo) {
      request['chipLot'] = batchInfo.lot;
    }
    if ('reference' in batchInfo) {
      request['chipReference'] = batchInfo.reference;
    }
  }

  /**
   * Disable a library (HU40)
   * Changes library status from CREATED to DISABLED and releases all specimens.
   * Endpoint: PUT /api/laboratorio/library/{libraryId}/disable
   *
   * @param libraryId The library UUID
   * @returns Observable that completes on success
   */
  disableLibrary(libraryId: string): Observable<void> {
    const url = `${this.baseUrl}/${libraryId}/disable`;

    return this.httpClient.put<void>(url, null).pipe(
      map(() => undefined),
      catchError((error) => {
        return this.errorHandler.handleError('disableLibrary', error);
      })
    );
  }

  /**
   * Map library edit backend response to domain model
   */
  private mapLibraryEditToModel(dto: LibraryEditBackendDto): LibraryForEdit {
    return {
      id: dto.id,
      code: dto.code,
      status: dto.status,
      cellId: dto.cellId,
      plateId: dto.plateId,
      indexId: dto.indexId,
      createdAt: new Date(dto.createdAt),
      specimens: dto.specimens.map((specimen) =>
        this.mapEditSpecimenDto(specimen)
      ),
      technology: dto.technology,
      chipType: dto.chipType,
      arrangementType: dto.arrangementType,
      plateName: dto.plateName,
      chipLot: dto.chipLot === '' ? null : (dto.chipLot ?? null),
      chipReference:
        dto.chipReference === '' ? null : (dto.chipReference ?? null),
      equipment: dto.equipment ?? null,
      mlpaCreationDate: dto.mlpaCreationDate
        ? new Date(dto.mlpaCreationDate)
        : null
    };
  }

  /**
   * Map edit specimen DTO to domain model
   */
  private mapEditSpecimenDto(
    dto: LibraryEditSpecimenBackendDto
  ): LibraryEditSpecimen {
    return {
      libSpecimenId: dto.libSpecimenId,
      specimenId: dto.specimenId,
      indexAdapterId: dto.indexAdapterId,
      lgCode: dto.lgCode,
      assignmentDate: dto.assignmentDate ? new Date(dto.assignmentDate) : null,
      indexName: dto.indexName,
      i5Bases: dto.i5Bases,
      i7Bases: dto.i7Bases,
      plateId: dto.plateId,
      indexId: dto.indexId,
      position: dto.position,
      positionLabel: dto.positionLabel
    };
  }

}

/**
 * @deprecated Use HttpLibraryRepository instead. Will be removed in future versions.
 */
export const HttpLibreriaRepository = HttpLibraryRepository;
